import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';

import { getDelistRelistStatus } from 'src/redux/slices/listing';

import Iconify from 'src/components/iconify';

import { FINAL_STATUSES, RESULTS_UNAVAILABLE } from './bulk-job-progress';

// Poll interval in milliseconds
const POLL_INTERVAL = 2000;

export default function DelistRelistProgress ({ batchId, onComplete, onClose }) {
  const dispatch = useDispatch();
  const [status, setStatus] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [error, setError] = useState(null);

  const isFinal = FINAL_STATUSES.includes(status?.status);

  // Fetch batch status
  const fetchStatus = useCallback(async () => {
    if (!batchId) return;

    try {
      const response = await dispatch(getDelistRelistStatus(batchId));
      if (response) {
        setStatus(response);
        setError(null);

        // If completed, trigger the onComplete callback with results
        if (response.status === 'completed' && response.results) {
          onComplete?.(response.results);
        } else if (response.status === 'failed') {
          setError(response.error || 'Something went wrong. Check My Listings before you try again.');
        }
      }
    } catch (err) {
      console.error('Failed to fetch delist-relist status:', err);
      setError(err.message || "Couldn't check how it's going. We'll keep trying.");
    }
  }, [batchId, dispatch, onComplete]);

  // The card stays mounted for the life of the app and only its batchId changes between runs, so
  // whatever belongs to the previous run is cleared the moment a new one starts. Its final status
  // in particular would otherwise stop the new run from ever being asked about.
  useEffect(() => {
    setIsDismissed(false);
    setIsMinimized(false);
    setStatus(null);
    setError(null);
  }, [batchId]);

  // Ask straight away, then keep asking until the job reaches a final state. Nothing is fetched
  // after that: the status thunk records the job's completion, so a repeat would count it twice.
  useEffect(() => {
    if (!batchId || isFinal) return undefined;

    fetchStatus();
    const interval = setInterval(fetchStatus, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [batchId, fetchStatus, isFinal]);

  // A dismissed card comes back when the job reaches a final state, so the results are seen.
  useEffect(() => {
    if (isDismissed && isFinal) {
      setIsDismissed(false);
    }
  }, [isDismissed, isFinal]);

  // Don't render if no batchId
  if (!batchId) return null;

  const isCompleted = status?.status === 'completed';
  const isFailed = status?.status === 'failed';
  // The server no longer has this run (its results are kept for an hour). The run may well have worked.
  const isResultsGone = status?.status === 'not_found';
  const isProcessing = status?.status === 'processing' || status?.status === 'queued';
  const progress = status?.progress || 0;
  const totalListings = status?.total || 0;

  // Get counts from results if available
  const results = status?.results;
  const successCount = results?.success || 0;
  const partialCount = results?.partial || 0;
  const failedCount = results?.failed || 0;
  const skippedCount = results?.skipped || 0;

  // Get status icon and color
  const getStatusDisplay = () => {
    if (isCompleted) {
      if (failedCount === 0 && partialCount === 0) {
        return { icon: 'solar:check-circle-bold', color: 'success.main', text: 'Completed' };
      }
      return { icon: 'solar:danger-triangle-bold', color: 'warning.main', text: 'Completed with issues' };
    }
    if (isFailed) {
      return { icon: 'solar:close-circle-bold', color: 'error.main', text: 'Failed' };
    }
    if (isResultsGone) {
      return { icon: 'solar:danger-triangle-bold', color: 'warning.main', text: 'Results not available' };
    }
    if (status?.status === 'processing') {
      return { icon: 'solar:refresh-bold', color: 'info.main', text: 'Delisting and relisting...' };
    }
    return { icon: 'solar:clock-circle-bold', color: 'text.secondary', text: 'Waiting to start' };
  };

  const statusDisplay = getStatusDisplay();

  // Get background color based on status
  const getBgColor = () => {
    if (isFailed) return 'error.lighter';
    if (isResultsGone) return 'warning.lighter';
    if (!isCompleted) return 'warning.lighter';
    if (failedCount > 0 || partialCount > 0) return 'warning.lighter';
    return 'success.lighter';
  };

  const handleDismissOrClose = () => {
    if (isFinal) {
      onClose();
    } else {
      setIsDismissed(true);
    }
  };

  // Don't render if dismissed (will reappear when job completes)
  if (isDismissed) return null;

  // Render minimized view
  if (isMinimized) {
    return (
      <Slide direction="up" in mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1300,
            borderRadius: 2,
            overflow: 'hidden',
            minWidth: 280
          }}
        >
          <Box
            sx={{
              p: 1.5,
              bgcolor: getBgColor(),
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
            onClick={() => setIsMinimized(false)}
          >
            {isProcessing && <CircularProgress size={16} />}
            {isFinal && (
              <Iconify icon={statusDisplay.icon} width={20} sx={{ color: statusDisplay.color }} />
            )}
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              Delist & Relist {(() => {
                if (isCompleted) return 'Complete';
                if (isFailed) return 'Failed';
                if (isResultsGone) return 'results not available';
                return `${progress}%`;
              })()}
            </Typography>
            <IconButton size="small" aria-label="Expand" onClick={(e) => { e.stopPropagation(); setIsMinimized(false); }}>
              <Iconify icon="eva:expand-outline" width={18} />
            </IconButton>
            <IconButton size="small" aria-label="Close" onClick={(e) => { e.stopPropagation(); handleDismissOrClose(); }}>
              <Iconify icon="eva:close-outline" width={18} />
            </IconButton>
          </Box>
        </Paper>
      </Slide>
    );
  }

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1300,
          borderRadius: 2,
          overflow: 'hidden',
          width: 340,
          maxHeight: '70vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            bgcolor: getBgColor(),
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Iconify icon="solar:refresh-bold" width={24} sx={{ color: statusDisplay.color }} />
          <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>
            Delist & Relist
          </Typography>
          <IconButton size="small" aria-label="Minimize" onClick={() => setIsMinimized(true)}>
            <Iconify icon="eva:minus-outline" width={18} />
          </IconButton>
          <IconButton size="small" aria-label="Close" onClick={handleDismissOrClose}>
            <Iconify icon="eva:close-outline" width={18} />
          </IconButton>
        </Box>

        {/* Progress bar (only when processing) */}
        {isProcessing && (
          <LinearProgress variant="determinate" value={progress} sx={{ height: 4 }} />
        )}

        {/* Content */}
        <CardContent sx={{ p: 2, overflow: 'auto', flex: 1 }}>
          {/* Error message */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isResultsGone && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {status.message || RESULTS_UNAVAILABLE}
            </Alert>
          )}

          {/* Status summary */}
          <Stack spacing={2}>
            {/* Progress stats */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                {isProcessing && <CircularProgress size={16} />}
                {isFinal && (
                  <Iconify icon={statusDisplay.icon} width={20} sx={{ color: statusDisplay.color }} />
                )}
                <Typography variant="body2" color="text.secondary">
                  {statusDisplay.text}
                </Typography>
              </Stack>
              {!isResultsGone && (
                <Typography variant="body2" fontWeight={600}>
                  {totalListings} listing{totalListings !== 1 ? 's' : ''}
                </Typography>
              )}
            </Box>

            {/* Success/Partial/Failed counts (when completed) */}
            {isCompleted && results && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {successCount > 0 && (
                  <Chip
                    size="small"
                    icon={<Iconify icon="solar:check-circle-bold" width={16} />}
                    label={`${successCount} relisted`}
                    color="success"
                    variant="soft"
                  />
                )}
                {partialCount > 0 && (
                  <Chip
                    size="small"
                    icon={<Iconify icon="solar:danger-triangle-bold" width={16} />}
                    label={`${partialCount} partly relisted`}
                    color="warning"
                    variant="soft"
                  />
                )}
                {failedCount > 0 && (
                  <Chip
                    size="small"
                    icon={<Iconify icon="solar:close-circle-bold" width={16} />}
                    label={`${failedCount} failed`}
                    color="error"
                    variant="soft"
                  />
                )}
                {skippedCount > 0 && (
                  <Chip
                    size="small"
                    icon={<Iconify icon="solar:minus-circle-bold" width={16} />}
                    label={`${skippedCount} skipped`}
                    color="default"
                    variant="soft"
                  />
                )}
              </Box>
            )}

            {/* Processing hint */}
            {isProcessing && (
              <Typography variant="caption" color="text.secondary">
                You can minimize or close this. It keeps going, and the results show up when it&apos;s done.
              </Typography>
            )}
          </Stack>
        </CardContent>

        {/* Footer actions */}
        {isCompleted && (
          <Box sx={{ p: 2, pt: 0 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={onClose}
              startIcon={<Iconify icon="solar:eye-bold" />}
            >
              View Results
            </Button>
          </Box>
        )}

        {(isFailed || isResultsGone) && (
          <Box sx={{ p: 2, pt: 0 }}>
            <Button
              fullWidth
              variant="outlined"
              color={isFailed ? 'error' : 'inherit'}
              onClick={onClose}
            >
              Close
            </Button>
          </Box>
        )}
      </Paper>
    </Slide>
  );
}

DelistRelistProgress.propTypes = {
  batchId: PropTypes.string,
  onComplete: PropTypes.func,
  onClose: PropTypes.func.isRequired
};
